import { useEffect, useState } from "react";
import type { PaymentMethod } from "@/mocks/dashboard";

type AddCardModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (card: PaymentMethod) => void;
};

const gradientPool = [
  "from-primary-700 to-primary-900",
  "from-accent-600 to-accent-800",
  "from-secondary-700 to-secondary-900",
  "from-foreground-800 to-foreground-950",
];

function detectBrand(number: string): string {
  const digits = number.replace(/\s+/g, "");
  if (/^4/.test(digits)) return "Visa";
  if (/^(5[1-5]|2[2-7])/.test(digits)) return "Mastercard";
  if (/^3[47]/.test(digits)) return "Amex";
  if (/^6(?:011|5)/.test(digits)) return "Discover";
  return "Card";
}

function formatCardNumber(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 19);
  return digits.replace(/(.{4})/g, "$1 ").trim();
}

function formatExpiry(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  if (digits.length < 3) return digits;
  return `${digits.slice(0, 2)} / ${digits.slice(2)}`;
}

export default function AddCardModal({ open, onClose, onSubmit }: AddCardModalProps) {
  const [number, setNumber] = useState("");
  const [holder, setHolder] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvc, setCvc] = useState("");
  const [makeDefault, setMakeDefault] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setNumber("");
      setHolder("");
      setExpiry("");
      setCvc("");
      setMakeDefault(true);
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const brand = detectBrand(number);
  const last4 = number.replace(/\D/g, "").slice(-4);
  const previewExpiry = expiry || "MM / YY";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const digitsOnly = number.replace(/\D/g, "");
    if (digitsOnly.length < 13) {
      setError("Please enter a valid card number.");
      return;
    }
    if (!holder.trim()) {
      setError("Cardholder name is required.");
      return;
    }
    if (!/^\d{2}\s*\/\s*\d{2}$/.test(expiry)) {
      setError("Expiry must be in MM / YY format.");
      return;
    }
    if (!/^\d{3,4}$/.test(cvc)) {
      setError("CVC must be 3 or 4 digits.");
      return;
    }
    setError(null);
    setSubmitting(true);
    setTimeout(() => {
      const newCard: PaymentMethod = {
        id: `pm-${Date.now()}`,
        brand,
        last4: last4.padStart(4, "•"),
        expiry,
        holder: holder.trim(),
        isDefault: makeDefault,
        color: gradientPool[Math.floor(Math.random() * gradientPool.length)],
      };
      onSubmit(newCard);
      setSubmitting(false);
      onClose();
    }, 700);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
      {/* overlay */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm cursor-pointer"
      />

      {/* panel */}
      <div className="relative bg-background-50 w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl border border-background-200 overflow-hidden animate-[slideUp_0.25s_ease-out]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
          <div>
            <h3 className="font-heading text-lg font-semibold text-foreground-950">
              Add a payment method
            </h3>
            <p className="text-xs text-foreground-600 mt-0.5">
              We charge after each visit. Cancel anytime.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-700 cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Live preview card */}
        <div className="px-5 pt-5">
          <div
            className={`relative rounded-2xl p-5 text-background-50 bg-gradient-to-br ${gradientPool[0]} min-h-[150px] flex flex-col justify-between`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="ri-bank-card-line text-2xl"></i>
                <span className="font-medium tracking-wide">{brand}</span>
              </div>
              {makeDefault && (
                <span className="inline-flex items-center bg-background-50/20 backdrop-blur px-2.5 py-1 rounded-full text-[10px] font-medium tracking-wide">
                  Default
                </span>
              )}
            </div>
            <div>
              <p className="font-heading text-xl tracking-widest">
                {number ? formatCardNumber(number).replace(/\d(?=\d{4})/g, "•") : "•••• •••• •••• ••••"}
              </p>
              <div className="flex items-center justify-between mt-3 text-xs">
                <div>
                  <p className="opacity-70">Cardholder</p>
                  <p className="font-medium mt-0.5">
                    {holder.trim() || "Your name"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="opacity-70">Expires</p>
                  <p className="font-medium mt-0.5">{previewExpiry}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-5 pt-5 pb-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">
              Card number
            </label>
            <div className="relative">
              <i className="ri-bank-card-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="cc-number"
                value={formatCardNumber(number)}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="1234 5678 9012 3456"
                className="w-full bg-background-100 border border-background-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">
              Cardholder name
            </label>
            <div className="relative">
              <i className="ri-user-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
              <input
                type="text"
                autoComplete="cc-name"
                value={holder}
                onChange={(e) => setHolder(e.target.value)}
                placeholder="Eleanor Hayes"
                className="w-full bg-background-100 border border-background-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                Expiry
              </label>
              <div className="relative">
                <i className="ri-calendar-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-exp"
                  value={expiry}
                  onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                  placeholder="MM / YY"
                  className="w-full bg-background-100 border border-background-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                CVC
              </label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
                <input
                  type="password"
                  inputMode="numeric"
                  autoComplete="cc-csc"
                  value={cvc}
                  onChange={(e) => setCvc(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="123"
                  className="w-full bg-background-100 border border-background-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400"
                />
              </div>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={makeDefault}
              onChange={(e) => setMakeDefault(e.target.checked)}
              className="w-4 h-4 rounded border-background-300 text-primary-600 focus:ring-primary-400 cursor-pointer"
            />
            <span className="text-sm text-foreground-800">
              Set as default payment method
            </span>
          </label>

          {error && (
            <div className="flex items-start gap-2 bg-accent-100/70 border border-accent-200 text-accent-900 text-xs rounded-lg px-3 py-2">
              <i className="ri-error-warning-line mt-0.5"></i>
              <span>{error}</span>
            </div>
          )}

          <div className="flex items-center gap-2 text-[11px] text-foreground-600">
            <i className="ri-shield-check-line text-primary-600"></i>
            <span>Encrypted & PCI-compliant. We never store your CVC.</span>
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-full text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-1.5 bg-foreground-950 hover:bg-foreground-900 text-background-50 px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-background-50/40 border-t-background-50 rounded-full animate-spin"></span>
                  Saving…
                </>
              ) : (
                <>
                  <i className="ri-add-line"></i> Save card
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}